import { applyValueRule, parseCellNumber } from './valueRule';
import { isValidLdThreshold, normalizeLdThreshold } from './columnMappings';

// `errorCode` of a prepared row (contract §5, precedence C30/C57), plus the
// threshold error the summary step adds once a row is edited.
export const ROW_ERRORS = Object.freeze({
  ANALYSIS_NOT_ON_SAMPLE: 'analysisNotOnSample',
  TARGET_OPERATION_NOT_FOUND: 'targetOperationNotFound',
  STATUS_NOT_EDITABLE: 'statusNotEditable',
  START_BEFORE_ACCEPTANCE: 'analysisStartBeforeAcceptance',
  AMBIGUOUS: 'analysisAmbiguous',
  DUPLICATE_TARGET: 'duplicateTarget',
  VALUE_NOT_NUMERIC: 'valueNotNumeric',
  LD_THRESHOLD: 'ldThreshold',
});

// Solved on the summary, by a choice or an edit. Any other error, unknown
// codes included, keeps the row out of the import.
const RESOLVABLE_ERRORS = Object.freeze([
  ROW_ERRORS.AMBIGUOUS,
  ROW_ERRORS.DUPLICATE_TARGET,
  ROW_ERRORS.VALUE_NOT_NUMERIC,
]);

export const ROW_STATES = Object.freeze({
  OK: 'ok',
  EXCLUDED: 'excluded',
  ERROR: 'error',
});

const PREPARED_ERROR = 'error';

const text = (value) => (value === null || value === undefined ? '' : value);

// A current value is there unless null or blank: the database has both.
export const hasCurrentValue = ({ currentValue }) =>
  text(currentValue).trim() !== '';

// A prepared row with what the summary edits: value and threshold as typed,
// unit, inclusion and the compliance of `checkedValue`.
export function createEntry(row, key) {
  return {
    key,
    row,
    valueText: text(row.rawValue),
    ldText: text(row.ldThreshold),
    measureId: row.measureId,
    included: row.included,
    compliant: row.compliant,
    checkedValue: row.value,
  };
}

export const isIncludable = ({ row }) =>
  row.status !== PREPARED_ERROR || RESOLVABLE_ERRORS.includes(row.errorCode);

const isEdited = ({ row, valueText, ldText }) =>
  valueText !== text(row.rawValue) || ldText !== text(row.ldThreshold);

export const isDirty = (entry) =>
  isEdited(entry) ||
  entry.measureId !== entry.row.measureId ||
  entry.included !== entry.row.included;

// The error an edit can fix, null when value and threshold are valid. As
// prepared, the backend already said: a value it could not read is null.
export function fieldError(entry) {
  if (!isEdited(entry)) {
    return entry.row.value === null ? ROW_ERRORS.VALUE_NOT_NUMERIC : null;
  }
  if (!isValidLdThreshold(normalizeLdThreshold(entry.ldText))) {
    return ROW_ERRORS.LD_THRESHOLD;
  }
  if (parseCellNumber(entry.valueText) === null) {
    return ROW_ERRORS.VALUE_NOT_NUMERIC;
  }
  return null;
}

// What REQ-10 writes: as prepared until edited, then computed again; nulls
// while value or threshold are not valid.
export function written(entry) {
  if (!isEdited(entry)) return { value: entry.row.value, LD: entry.row.LD };
  if (fieldError(entry)) return { value: null, LD: null };
  return applyValueRule(
    parseCellNumber(entry.valueText),
    normalizeLdThreshold(entry.ldText),
    entry.row.decimals
  );
}

// ── Conflicts ─────────────────────────────────────────────────────────────

const groupId = ({ sourceRow, column }) => `${sourceRow}\u0000${column}`;

function keysBy(entries, idOf) {
  const byId = new Map();
  entries.forEach((entry) => {
    const id = idOf(entry.row);
    if (id === null) return;
    byId.set(id, [...(byId.get(id) || []), entry.key]);
  });
  return [...byId.values()].filter((keys) => keys.length > 1);
}

// Rows that cannot both be written, by key: the candidates of the same
// ambiguous cell (C28, C57) and the rows of the same value row (REQ-14).
export function conflictsOf(entries) {
  const groups = keysBy(entries, groupId);
  const collisions = keysBy(
    entries,
    ({ operationValueId }) => operationValueId
  );
  const toMap = (sets) => {
    const map = new Map();
    sets.forEach((keys) =>
      keys.forEach((key) =>
        map.set(key, [
          ...(map.get(key) || []),
          ...keys.filter((other) => other !== key),
        ])
      )
    );
    return map;
  };
  return { groups: toMap(groups), collisions: toMap(collisions) };
}

// Every candidate of the ambiguous group of a row, in order; null outside one.
export function groupKeysOf(conflicts, key) {
  const others = conflicts.groups.get(key);
  return others ? [key, ...others].sort((a, b) => a - b) : null;
}

const othersOf = (conflicts, key) => [
  ...(conflicts.groups.get(key) || []),
  ...(conflicts.collisions.get(key) || []),
];

// Including a row excludes every row in conflict with it.
export function inclusionChanges(conflicts, key, included) {
  const changes = { [key]: included };
  if (included) {
    othersOf(conflicts, key).forEach((other) => {
      changes[other] = false;
    });
  }
  return changes;
}

// The candidate chosen in an ambiguous group, null for "Non importare".
export function choiceChanges(conflicts, groupKeys, chosenKey) {
  const changes = {};
  groupKeys.forEach((key) => {
    changes[key] = false;
  });
  return chosenKey === null
    ? changes
    : { ...changes, ...inclusionChanges(conflicts, chosenKey, true) };
}

export function rowState(entry, conflicts, entriesByKey) {
  const { row, key } = entry;
  if (!isIncludable(entry)) {
    return { state: ROW_STATES.ERROR, errorCode: row.errorCode };
  }
  const error = fieldError(entry);
  if (entry.included) {
    return error
      ? { state: ROW_STATES.ERROR, errorCode: error }
      : { state: ROW_STATES.OK };
  }
  const anyIncluded = (keys) => keys.some((k) => entriesByKey[k].included);
  const group = conflicts.groups.get(key);
  if (group && !anyIncluded(group)) {
    return { state: ROW_STATES.ERROR, errorCode: ROW_ERRORS.AMBIGUOUS };
  }
  const collisions = conflicts.collisions.get(key);
  if (collisions && !anyIncluded(collisions)) {
    return { state: ROW_STATES.ERROR, errorCode: ROW_ERRORS.DUPLICATE_TARGET };
  }
  if (anyIncluded(othersOf(conflicts, key))) {
    return { state: ROW_STATES.EXCLUDED };
  }
  return error
    ? { state: ROW_STATES.ERROR, errorCode: error }
    : { state: ROW_STATES.EXCLUDED };
}

// ── Order and pages ───────────────────────────────────────────────────────

const isResolvableError = ({ row }) =>
  row.status === PREPARED_ERROR && RESOLVABLE_ERRORS.includes(row.errorCode);

// Errors first (REQ-17), those the user can act on ahead (C61): a unit with a
// choice or an edit to make, then the units this step cannot import (analysis
// not on the sample, status, …), then the units with something to write.
function unitTier(unit, entries) {
  const unitEntries = unit.map((k) => entries[k]);
  if (unitEntries.some(isResolvableError)) return 0;
  return unitEntries.every(({ row }) => row.status === PREPARED_ERROR) ? 1 : 2;
}

// Rows in conflict stay together: a unit is a row or a set of rows in
// conflict. Units go by tier, each tier in the order of the response; inside
// a unit the candidates of a group are next to each other, so their choice
// spans their rows.
export function orderedUnits(entries, conflicts) {
  const seen = new Set();
  const units = [];
  entries.forEach(({ key }) => {
    if (seen.has(key)) return;
    const unit = [];
    const queue = [key];
    while (queue.length) {
      const current = queue.shift();
      if (!seen.has(current)) {
        seen.add(current);
        unit.push(current);
        queue.push(...othersOf(conflicts, current));
      }
    }
    const firstOfGroup = (k) => Math.min(k, ...(conflicts.groups.get(k) || []));
    unit.sort((a, b) => firstOfGroup(a) - firstOfGroup(b) || a - b);
    units.push(unit);
  });
  return [0, 1, 2].flatMap((tier) =>
    units.filter((unit) => unitTier(unit, entries) === tier)
  );
}

// ── Totals ────────────────────────────────────────────────────────────────

export function summaryCounts(entries, states) {
  const included = entries.filter((entry) => entry.included);
  const errors = entries.filter(
    ({ key }) => states[key].state === ROW_STATES.ERROR
  );
  return {
    toWrite: included.length,
    overwrites: included.filter(({ row }) => hasCurrentValue(row)).length,
    errors: errors.length,
    nonCompliant: included.filter(({ compliant }) => compliant === false)
      .length,
    blocked: errors.filter(({ included: isIn }) => isIn).length,
  };
}

// The included rows, with what was edited on them.
export function rowsToWrite(entries) {
  return entries
    .filter((entry) => entry.included)
    .map((entry) => ({
      ...entry.row,
      ...written(entry),
      ldThreshold: isEdited(entry)
        ? normalizeLdThreshold(entry.ldText)
        : entry.row.ldThreshold,
      measureId: entry.measureId,
      included: true,
      compliant: entry.compliant,
    }));
}
